import React, {Fragment} from "react";
import { withStyles, Button } from "@material-ui/core";
import styles from "../../styles";
import TerminalDropdown from "../../TerminalDropdown";
import RestoreIcon from "@material-ui/icons/Restore";
import {getDataWithQueryString, postData} from "../../../../../api/componentAPIs";
import { convertDateTimeToTimeString, isTimeValid } from '@dataGrid/gridUtilities';
import { NumericEditor, DateTimeEditor } from '@dataGrid/components/cellEditors';
import appConstant from "../../../../../appConstants";
import MESSAGES from "../../../../../appMessages";
import { withUserProfile } from "../../../../../userProfile/userProfileContext";
import { compose } from "recompose";
import { withSnackbar } from 'notistack';
import Tooltip from "../../../../common/StyledToolTip";
import { observer, inject } from "mobx-react";

const waveStyles = theme => ({
    contentContainer:{
        padding: "16px 0",
        display:"flex",
        flexDirection: "column"
    },
    waveContainer:{
        padding: "8px 0",
        display:"inline-flex",
        '&>*':{
            maxWidth: 100,
            marginRight: 5,
             marginTop:0
        }
    },
    waveName:{
        color: theme.HeaderSelectedText,
        fontSize: 14,
        width: 100
    },
    waveValue:{
        color: theme.TextColor,
        fontSize: 14,
        width: 100,
    },
    actionIcon:{
        color: theme.iconColor,
        paddingRight: 5,
        cursor: "pointer"
    },
    newWaveActionButton:{
        textTransform: "none",
        paddingTop:0,
        paddingBottom:0,
        height: 24
    },
    waveErrorMessage:{
        maxHeight: 40,
        maxWidth: 200
    }
})

@inject ('CommonStore')
@observer

class Waves extends React.Component {
    constructor(props){
        super(props);
        this.state ={
            terminalID: 1,
            addNewWave: false,
            errorMessage: null
        }
        this.timer = null;
    }

    componentDidMount(){
        this._isComponentMounted = true;
    }

    componentWillUnmount(){
        this._isComponentMounted = false;
        clearTimeout(this.timer);
    }

    componentDidUpdate(prevProps) {
        if (this.props.userData.userProfile.scenarioID !== prevProps.userData.userProfile.scenarioID) {
          this.cancelUpdate();
        }
    }

    addWave = ( ) => {
        if(!this.state.addNewWave){
            this.setState({
                addNewWave: true,
                editWaveId: null
            })
        }
    }

    cancelUpdate = () => {
        this.setState({
            addNewWave: false,
            editWaveId: null
        });
    }

    editWave = event => {
        const editWaveId = event.currentTarget.getAttribute("data-waveid") || null;
        this.setState({
            editWaveId: Number(editWaveId),
            addNewWave: false
        })
    }

    isWaveNameUnique = (waveData, waveName, waveId) => {
        waveId = waveId ? Number(waveId) : null;
        return waveData.find(wave => wave.scenarioWaveId !== waveId && wave.waveName.toString().toLowerCase() === waveName.toString().toLowerCase());
    }

    isWaveTimeUnique = (waveData, waveTime, waveId) => {
        waveId = waveId ? Number(waveId) : null;
        waveTime = convertDateTimeToTimeString(waveTime);
        return waveData.find(wave => wave.scenarioWaveId !== waveId && convertDateTimeToTimeString(wave.ibetaCutOffTime) === waveTime);
    }

    isWaveDataChanged = ({waveData, waveId, waveName, waveTime}) => {
        waveId = Number(waveId);
        const wave = waveData.find(wave => wave.scenarioWaveId === waveId);
        return wave.waveName !== waveName || convertDateTimeToTimeString(wave.ibetaCutOffTime) !== convertDateTimeToTimeString(waveTime);
    }

    isValidUpdate = ({waveId, waveName, waveTime}) => {
        const waveData = this.props.CommonStore.wavesData.length ? this.props.CommonStore.wavesData.slice() : [];
        if(!waveName || !waveTime || !isTimeValid(convertDateTimeToTimeString(waveTime))){
            this.props.enqueueSnackbar(MESSAGES.WAVE.UPDATE_INVALID, {variant: "error"});
            return false;
        } else if(this.isWaveNameUnique(waveData, waveName,waveId)){
            this.props.enqueueSnackbar(MESSAGES.WAVE.UPDATE_WAVENAME_UNIQUE, {variant: "error"});
            return false;
        } else if(this.isWaveTimeUnique(waveData, waveTime,waveId)){
            this.props.enqueueSnackbar(MESSAGES.WAVE.UPDATE_WAVETIME_UNIQUE, {variant: "error"});
            return false;
        } else if(waveId && !this.isWaveDataChanged({waveData, waveId, waveName, waveTime})){
            this.props.enqueueSnackbar(MESSAGES.COMMON.NO_CHANGES_TO_SAVE, {variant: "info"});
            this.cancelUpdate();
            return false;
        }
        return true;
    }


    updateWave = (event) => {
        const waveId = event.currentTarget.getAttribute("data-waveid") || null;
        const waveName = this.waveNameRef.getValue();
        const waveTime = this.waveTimeRef.getValue();

        if(!this.isValidUpdate({waveId, waveName, waveTime})){
            return;
        }

        const requestParams = {
            scenarioId: this.props.userData.userProfile.scenarioID,
            waveDetail:{
                scenarioWaveID: waveId,
                waveName: waveName,
                IbEtaCutOffTime: convertDateTimeToTimeString(waveTime),
            }
        }

        postData("UpdateWave", requestParams).then(response =>{
            if(response.status === 200){
                this.props.enqueueSnackbar(`${waveId ? "Updated" : "Added"} wave ${waveName}`, {variant: "success"});
                if(this._isComponentMounted){
                    this.props.CommonStore.updateWaveData(response.data);
                    this.setState({
                        addNewWave: false,
                        editWaveId: null
                    });
                }
                return;
            }
            this.handleUpdateWaveError({waveId, waveName, statusCode: response.status});
        }, () => {
            this.handleUpdateWaveError({waveId, waveName});
        })
    }

    handleUpdateWaveError = ({waveId, waveName, statusCode}) => {
        if (statusCode === 403) {
            return;
        }
        this.props.enqueueSnackbar(`Error ${waveId ? "updating" : "adding"} wave ${waveName}`, {variant: "error"});
    }

    deleteWave = (event) => {
        const waveData = this.props.CommonStore.wavesData.length ? this.props.CommonStore.wavesData : [];
        if(waveData.length === 1){
            this.props.enqueueSnackbar(MESSAGES.WAVE.UPDATE_DELETE_LAST_WAVE, {variant: "error"});
            return;
        }

        const waveId = event.currentTarget.getAttribute("data-waveid") || null;
        const wave = waveData.find(wave => wave.scenarioWaveId === Number(waveId));
        const requestParams = {
            scenarioId: this.props.userData.userProfile.scenarioID,
            scenarioWaveId: wave.scenarioWaveId
        }

        postData("DeleteWave", requestParams).then(response =>{
            if(response.status === 200){
                this.props.enqueueSnackbar(`Wave ${wave.waveName} deleted`, {variant: "success"});
                if(this._isComponentMounted){
                    this.props.CommonStore.updateWaveData(response.data);
                    this.setState({
                        addNewWave: false,
                        editWaveId: null
                    });
                }
                return;
            }
            this.handleDeleteWaveError(wave.waveName, response.status);
        }, () => {
            this.handleDeleteWaveError(wave.waveName);
        })
    }

    handleDeleteWaveError = (waveName, statusCode) => {
        if (statusCode === 403) {
            return;
        }
        this.props.enqueueSnackbar(`Error deleting wave ${waveName}`, {variant: "error"});
    }

    waveUpdateRenderer = ({isEdit, wave }) => {
        const { classes } = this.props;
        return (
          <div className={classes.waveContainer}>
            <NumericEditor
              ref={(inputRef) => {
                this.waveNameRef = inputRef;
                this.timer = setTimeout(() => {
                  // needed for focusing on the text input
                  this.waveNameRef && this.waveNameRef.focusOnInput();
                }, 0);
              }}
              value={wave ? wave.waveName : ''}
            />
            <DateTimeEditor
              disableFocusInput
              ref={(inputRef) => (this.waveTimeRef = inputRef)}
              dateType={appConstant.DATETYPES.TIME}
              value={wave ? convertDateTimeToTimeString(wave.ibetaCutOffTime) : ''}
            />
            <Button
              color="primary"
              variant="contained"
              size="small"
              classes={{ root: classes.newWaveActionButton }}
              data-waveid={wave ? wave.scenarioWaveId : null}
              onClick={this.updateWave}>
              {isEdit ? 'Update' : 'Add'}
            </Button>
            <Button
              color="primary"
              variant="outlined"
              size="small"
              classes={{ root: classes.newWaveActionButton }}
              data-waveid={null}
              onClick={this.cancelUpdate}>
              Cancel
            </Button>
          </div>
        );
    }

    render(){
        const { classes } = this.props;
        const waveData = this.props.CommonStore.wavesData.length ? this.props.CommonStore.wavesData.slice() : [];
        return (
            <Fragment>
            <header className={classes.moduleHeader}  data-automationid="waves-title"> Waves </header>
            <div className={classes.moduleContainer}>
                <div className={classes.terminalContainer}>
                    <span className={classes.terminalText}> Terminal : </span>
                    <span className={classes.terminalDropdown}>
                        <TerminalDropdown onChange={this.onTerminalChange} selectedTerminalID={this.state.terminalID} />
                    </span>
                </div>
                <div className={classes.contentContainer}>
                    {
                        waveData && waveData.map(wave => (
                             this.state.editWaveId === wave.scenarioWaveId ? (
                                this.waveUpdateRenderer({isEdit: true, wave})
                             ):
                            (
                            <div key={wave.scenarioWaveId} className={classes.waveContainer}>
                                <div className={classes.waveName} > { wave.waveName }</div>
                                <div className={classes.waveValue}> { convertDateTimeToTimeString(wave.ibetaCutOffTime) } </div>
                                <div className={classes.waveActions}>
                                    <Tooltip title="Edit">
                                        <i className={`icon-icon_edit ${classes.actionIcon}`}  data-waveid={wave.scenarioWaveId} onClick={this.editWave}/>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <i className={`icon-icon_delete ${classes.actionIcon}`}  data-waveid={wave.scenarioWaveId} onClick={this.deleteWave}/>
                                    </Tooltip>
                                </div>
                            </div>
                            )
                        ))
                    }
                    {
                          this.state.addNewWave && (
                                this.waveUpdateRenderer({isEdit: false, wave:null})
                        )
                    }
                    { this.state.errorMessage && (
                        <div className={`${classes.errorMessageContainer} ${classes.waveErrorMessage}`}>
                            <div className={classes.errorMessage}> {this.state.errorMessage} </div>
                        </div>
                    )}

                </div>
                <div className={classes.action}>
                    <Button color="primary" variant="outlined" size="small" classes={{root: classes.actionButton}} onClick={this.addWave} data-automationid="addWave">
                        Add wave
                    </Button>
                </div>
                <div className={classes.action}>
                     <Button color="primary" variant="contained" size="small" classes={{root: classes.actionButton, disabled: classes.actionDisabled}} disabled>
                        <RestoreIcon classes={{root: classes.restoreIcon}} /> Restore to Defaults
                    </Button>
                </div>

            </div>
            </Fragment>
        )
    }
}

export default compose(withStyles((theme) => ({
    ...styles(theme),
    ...waveStyles(theme),
})), withUserProfile, withSnackbar)(Waves)